/**
 * Hook para manejar configuración de retiros (límites y comisiones)
 * Similar a use-commission-fees pero para withdrawals
 */
"use client";

import { useState, useEffect } from 'react';
import type { CommissionFees } from './use-commission-fees';

export interface WithdrawalLimits {
  min: number;
  max: number;
}

export interface WithdrawalConfig {
  limits: WithdrawalLimits | null;
  fees: CommissionFees | null;
  currentFee: number;
  netAmount: number; // Lo que el usuario recibe (amount - fee)
  totalDeducted: number; // Lo que se descuenta del balance (amount)
  isLoading: boolean;
  error: string | null;
}

export function useWithdrawalConfig(amount: number): WithdrawalConfig {
  const [limits, setLimits] = useState<WithdrawalLimits | null>(null);
  const [fees, setFees] = useState<CommissionFees | null>(null);
  const [currentFee, setCurrentFee] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar límites y fees del backend
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true);
        
        // Cargar fees (misma lógica que deposits)
        const feesResponse = await fetch('/api/getCommissionFees');
        const feesData = await feesResponse.json();

        if (feesResponse.ok) {
          console.log('[WithdrawalConfig] Loaded fees from backend:', feesData);
          setFees(feesData);
          setError(null);
        } else {
          console.error('[WithdrawalConfig] Error loading fees:', feesData);
          setError(feesData.message || 'Error loading withdrawal config');
        }

        // Límites hardcoded por ahora (10-10000 USDT)
        // TODO: Obtener del backend si es necesario
        setLimits({ min: 10, max: 10000 });
        
      } catch (err) {
        console.error('Error loading withdrawal config:', err);
        setError('Network error loading withdrawal config');
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, []);

  // Calcular fee basado en el monto (misma lógica que deposits)
  useEffect(() => {
    if (!fees || !amount || amount <= 0) {
      setCurrentFee(0);
      return;
    }

    let fee = 0;

    // Aplicar comisiones por rangos
    if (amount <= 30) {
      fee = fees.fee_one;
      console.log(`[WithdrawalConfig] Amount ${amount} <= 30, using fee_one: ${fee}`);
    } else if (amount <= 250) {
      fee = fees.fee_two;
      console.log(`[WithdrawalConfig] Amount ${amount} <= 250, using fee_two: ${fee}`);
    } else if (amount <= 1000) {
      fee = fees.fee_three;
      console.log(`[WithdrawalConfig] Amount ${amount} <= 1000, using fee_three: ${fee}`);
    } else {
      fee = fees.fee_three;
      console.log(`[WithdrawalConfig] Amount ${amount} > 1000, using fee_three: ${fee}`);
    }

    console.log(`[WithdrawalConfig] Final fee calculation - Amount: ${amount}, Fee: ${fee}`);
    setCurrentFee(fee);
  }, [fees, amount]);

  return {
    limits,
    fees,
    currentFee,
    netAmount: amount - currentFee, // Usuario recibe esto
    totalDeducted: amount, // Se descuenta esto del balance
    isLoading,
    error,
  };
}
